import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Chip,
  Icon,
  Switch,
  Textfield
} from '@clientbase/clientbase-library'
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography
} from '@mui/material'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import { useFinishCoupon } from '../../api/useFinishCoupon'
import { useListCoupons } from '../../api/useListCoupons'
import { usePauseCoupon } from '../../api/usePauseCoupon'
import { useReactivateCoupon } from '../../api/useReactivateCoupon'
import { useUnpauseCoupon } from '../../api/useUnpauseCoupon'
import { Coupon } from '../../types/coupon'
import { formatCouponPercent } from '../../utils/formatCouponPercent'
import { usosLabel } from '../../utils/usosLabel'
import CouponDetailsModal from '../CouponDetailsModal/CouponDetailsModal'
import CouponFormModal from '../CouponFormModal/CouponFormModal'

interface CouponsAccordionProps {
  linkUuid?: string
  couponsEnabled: boolean
  onCouponsEnabledChange?: (enabled: boolean) => void
  associatedCoupons: Coupon[]
  onCouponsChange?: (coupons: Coupon[]) => void
  readOnly?: boolean
  isDetails?: boolean
  totalAmount?: number
}

const formatCouponLabel = (coupon: Coupon) => {
  if (coupon.discount_type === 'percentage') {
    const label = `${coupon.code} ${formatCouponPercent(coupon.discount_value)}`
    return coupon.max_discount
      ? `${label} (max ${formatMoney(coupon.max_discount)})`
      : label
  }
  return `${coupon.code} ${formatMoney(coupon.discount_value)}`
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Ativo',
  paused: 'Pausado',
  expired: 'Expirado',
  exhausted: 'Esgotado',
  finished: 'Finalizado'
}

const STATUS_COLORS: Record<string, string> = {
  active: theme.palette.success[300],
  paused: theme.palette.alert[300],
  expired: theme.palette.neutral[400],
  exhausted: theme.palette.error[300],
  finished: theme.palette.neutral[500]
}

const formatDiscountHeadline = (coupon: Coupon): string => {
  if (coupon.discount_type === 'percentage') {
    return formatCouponPercent(coupon.discount_value)
  }
  return formatMoney(coupon.discount_value)
}

const formatDiscountDetail = (coupon: Coupon): string | null => {
  if (coupon.discount_type === 'percentage' && coupon.max_discount) {
    return `max ${formatMoney(coupon.max_discount)}`
  }
  return null
}

const formatQuota = (coupon: Coupon): string => {
  if (coupon.max_usage) {
    return `${coupon.usage_count}/${coupon.max_usage} ${usosLabel(
      coupon.max_usage
    )}`
  }
  return `${coupon.usage_count} ${usosLabel(coupon.usage_count)}`
}

const getUsagePct = (coupon: Coupon): number | null => {
  if (!coupon.max_usage) return null
  return (coupon.usage_count / coupon.max_usage) * 100
}

const getExpiryPct = (coupon: Coupon): number | null => {
  if (!coupon.expires_at) return null
  const start = new Date(coupon.created_at).getTime()
  const end = new Date(coupon.expires_at).getTime()
  const now = Date.now()
  if (end <= start) return 100
  const elapsed = ((now - start) / (end - start)) * 100
  return Math.min(100, Math.max(0, elapsed))
}

const formatExpiry = (coupon: Coupon): string | null => {
  if (!coupon.expires_at) return null
  const date = new Date(coupon.expires_at)
  const now = new Date()
  const diffDays = Math.ceil(
    (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  )
  const dateLabel = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short'
  })
  if (diffDays < 0) return `Expirou em ${dateLabel}`
  if (diffDays === 0) return 'Expira hoje'
  if (diffDays <= 30) return `Expira em ${diffDays}d (${dateLabel})`
  return `Expira em ${dateLabel}`
}

const calculateCouponDiscount = (
  coupon: Coupon,
  baseAmount: number
): { discount: number; finalValue: number } => {
  if (baseAmount <= 0) return { discount: 0, finalValue: 0 }

  let discount: number
  if (coupon.discount_type === 'percentage') {
    discount = baseAmount * (Number(coupon.discount_value) / 100)
    if (coupon.max_discount && discount > coupon.max_discount) {
      discount = coupon.max_discount
    }
  } else {
    discount = Number(coupon.discount_value)
  }

  discount = Math.min(discount, baseAmount)
  const finalValue = Math.max(baseAmount - discount, 0)

  return { discount, finalValue }
}

const CouponsAccordion: React.FC<CouponsAccordionProps> = ({
  couponsEnabled,
  onCouponsEnabledChange,
  associatedCoupons,
  onCouponsChange,
  readOnly = false,
  isDetails = false,
  totalAmount
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [autocompleteOpen, setAutocompleteOpen] = useState(false)
  const [createCouponModalOpen, setCreateCouponModalOpen] = useState(false)
  const [detailsModal, setDetailsModal] = useState<{
    open: boolean
    coupon: Coupon | null
  }>({ open: false, coupon: null })

  const {
    coupons: availableCoupons,
    loading: loadingCoupons,
    refetch
  } = useListCoupons({ status: 'active', search: searchTerm })

  const { pauseCoupon } = usePauseCoupon()
  const { unpauseCoupon } = useUnpauseCoupon()
  const { finishCoupon } = useFinishCoupon()
  const { reactivateCoupon } = useReactivateCoupon()

  const updateAssociatedStatus = (uuid: string, status: Coupon['status']) => {
    onCouponsChange?.(
      associatedCoupons.map((c) => (c.uuid === uuid ? { ...c, status } : c))
    )
  }

  const handleTransitionError = (err: any) => {
    const message = typeof err === 'string' ? err : 'Erro ao realizar ação'
    toast.error(
      message === 'invalid_transition'
        ? 'Não é possível realizar esta ação no estado atual do cupom'
        : message
    )
  }

  const handlePause = async (coupon: Coupon) => {
    try {
      const result = await pauseCoupon(coupon.uuid)
      if (result) {
        toast.success(`Cupom ${coupon.code} pausado`)
        updateAssociatedStatus(coupon.uuid, 'paused')
      }
    } catch (err) {
      handleTransitionError(err)
    }
  }

  const handleUnpause = async (coupon: Coupon) => {
    try {
      const result = await unpauseCoupon(coupon.uuid)
      if (result) {
        toast.success(`Cupom ${coupon.code} reativado`)
        updateAssociatedStatus(coupon.uuid, 'active')
      }
    } catch (err) {
      handleTransitionError(err)
    }
  }

  const handleFinish = async (coupon: Coupon) => {
    try {
      const result = await finishCoupon(coupon.uuid)
      if (result) {
        toast.success(`Cupom ${coupon.code} finalizado`)
        updateAssociatedStatus(coupon.uuid, 'finished')
      }
    } catch (err) {
      handleTransitionError(err)
    }
  }

  const handleReactivate = async (coupon: Coupon) => {
    try {
      const result = await reactivateCoupon(coupon.uuid)
      if (result) {
        toast.success(`Cupom ${coupon.code} reativado`)
        updateAssociatedStatus(coupon.uuid, 'active')
      }
    } catch (err) {
      handleTransitionError(err)
    }
  }

  useEffect(() => {
    if (!autocompleteOpen) return

    const timer = setTimeout(() => {
      refetch({ status: 'active', search: searchTerm })
    }, 400)
    return () => clearTimeout(timer)
  }, [searchTerm, autocompleteOpen])

  const filteredCoupons = availableCoupons.filter(
    (coupon) => !associatedCoupons.some((ac) => ac.uuid === coupon.uuid)
  )

  const handleAssociate = useCallback(
    (coupon: Coupon | null) => {
      if (!coupon) return
      onCouponsChange?.([...associatedCoupons, coupon])
      setSearchTerm('')
    },
    [associatedCoupons, onCouponsChange]
  )

  const handleRemoveClick = (coupon: Coupon) => {
    onCouponsChange?.(associatedCoupons.filter((c) => c.uuid !== coupon.uuid))
  }

  const canEdit = couponsEnabled && !readOnly
  const title = isDetails ? 'Cupons' : 'Cupons de desconto'

  return (
    <>
      <Accordion
        {...(isDetails
          ? { defaultExpanded: associatedCoupons.length > 0 }
          : {
              expanded: couponsEnabled,
              onChange: (_, expanded) => onCouponsEnabledChange?.(expanded)
            })}
        sx={{
          border: '1px solid #E8EBE8',
          borderLeft: '3px solid',
          borderLeftColor: 'primary.main',
          borderRadius: '8px !important',
          backgroundColor: '#FAFBFA',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            backgroundColor: '#fff'
          }
        }}
      >
        <AccordionSummary
          aria-controls="coupons-content"
          id="coupons"
          data-cy="coupons-accordion-summary"
          {...(!isDetails && {
            hasSwitch: true,
            expandIcon: (
              <Switch
                checked={couponsEnabled}
                onChange={(e) => onCouponsEnabledChange?.(e.target.checked)}
                disabled={readOnly}
              />
            )
          })}
          sx={
            !isDetails
              ? {
                  '.MuiAccordionSummary-content': { margin: '20px 0px' }
                }
              : undefined
          }
        >
          <Box display="flex" alignItems="center" gap="8px">
            {isDetails && (
              <Box
                sx={{
                  width: 35,
                  height: 35,
                  background: '#F8F8F8',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Icon icon="paid" />
              </Box>
            )}
            <Typography variant={isDetails ? 'smLight' : 'smBold'}>
              {title}
            </Typography>
            {!isDetails && associatedCoupons.length > 0 && (
              <Chip
                variant="info"
                label={String(associatedCoupons.length)}
                sx={{
                  height: '20px',
                  minWidth: '20px',
                  fontSize: '11px'
                }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Stack spacing={2}>
            {/* Autocomplete to add coupons */}
            {canEdit && !isDetails && (
              <Box>
                <Autocomplete
                  id="coupon_select"
                  open={autocompleteOpen}
                  onOpen={() => setAutocompleteOpen(true)}
                  onClose={() => setAutocompleteOpen(false)}
                  options={[
                    { __create_new_coupon__: true } as any,
                    ...filteredCoupons
                  ]}
                  getOptionLabel={(option) => {
                    const opt = option as any
                    if (opt?.__create_new_coupon__) return ''
                    return formatCouponLabel(opt as Coupon)
                  }}
                  loading={loadingCoupons}
                  inputValue={searchTerm}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') setSearchTerm(value)
                  }}
                  onChange={(_, value) => {
                    const opt = value as any
                    if (!opt || opt.__create_new_coupon__) return
                    handleAssociate(opt as Coupon)
                  }}
                  filterOptions={(options, state) => {
                    return (options as any[]).filter((opt) => {
                      if (opt.__create_new_coupon__) return true
                      return (opt as Coupon).code
                        ?.toLowerCase()
                        .includes(state.inputValue.toLowerCase())
                    })
                  }}
                  value={null}
                  blurOnSelect
                  noOptionsText={
                    <Box
                      onClick={() => setCreateCouponModalOpen(true)}
                      sx={{ cursor: 'pointer', mx: -2, mt: '-3px' }}
                    >
                      <Stack width="100%">
                        <Button
                          fullWidth
                          variantButton="ghost"
                          sx={{ justifyContent: 'flex-start' }}
                        >
                          <Stack direction="row" gap="5px" alignItems="center">
                            <Icon
                              icon="add"
                              sx={{
                                color: theme.palette.neutral[400],
                                fontSize: '18px'
                              }}
                            />
                            <Typography
                              sx={{
                                color: theme.palette.neutral[400],
                                fontWeight: '600',
                                fontSize: '14px'
                              }}
                            >
                              Novo cupom
                            </Typography>
                          </Stack>
                        </Button>
                      </Stack>
                    </Box>
                  }
                  loadingText="Buscando cupons..."
                  renderOption={(props, option) => {
                    const opt = option as any
                    if (opt?.__create_new_coupon__) {
                      return (
                        <Box
                          component="li"
                          key="create-new-coupon"
                          {...props}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            setCreateCouponModalOpen(true)
                          }}
                          sx={{
                            width: '100%',
                            borderBottom: `solid 1px ${theme.palette.neutral[700]}`,
                            mt: '-8px'
                          }}
                        >
                          <Stack width="100%">
                            <Button
                              fullWidth
                              variantButton="ghost"
                              sx={{ justifyContent: 'flex-start' }}
                            >
                              <Stack
                                direction="row"
                                gap="5px"
                                alignItems="center"
                              >
                                <Icon
                                  icon="add"
                                  sx={{
                                    color: theme.palette.neutral[400],
                                    fontSize: '18px'
                                  }}
                                />
                                <Typography
                                  sx={{
                                    color: theme.palette.neutral[400],
                                    fontWeight: '600',
                                    fontSize: '14px'
                                  }}
                                >
                                  Novo cupom
                                </Typography>
                              </Stack>
                            </Button>
                          </Stack>
                        </Box>
                      )
                    }
                    const c = opt as Coupon
                    const optStatusColor =
                      STATUS_COLORS[c.status] || theme.palette.neutral[500]
                    const optExpiry = formatExpiry(c)
                    return (
                      <Box
                        component="li"
                        {...props}
                        key={c.uuid}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch !important',
                          gap: '2px',
                          padding: '8px 12px !important'
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          width="100%"
                          alignItems="center"
                          gap={1}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap="8px"
                            minWidth={0}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                letterSpacing: '0.2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {c.code}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: optStatusColor,
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase',
                                '&::before': {
                                  content: '""',
                                  display: 'inline-block',
                                  width: '5px',
                                  height: '5px',
                                  borderRadius: '50%',
                                  backgroundColor: optStatusColor
                                }
                              }}
                            >
                              {STATUS_LABELS[c.status]}
                            </Box>
                          </Box>
                          <Box
                            display="flex"
                            alignItems="baseline"
                            gap="4px"
                            color={optStatusColor}
                          >
                            <Typography
                              component="span"
                              sx={{ fontSize: '14px', fontWeight: 700 }}
                            >
                              {formatDiscountHeadline(c)}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '12px',
                                fontWeight: 700,
                                letterSpacing: '0.6px',
                                opacity: 0.75
                              }}
                            >
                              {c.discount_type === 'percentage'
                                ? 'OFF'
                                : 'FIXO'}
                            </Typography>
                          </Box>
                        </Stack>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap="10px"
                          sx={{
                            fontSize: '12px',
                            color: theme.palette.neutral[400]
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                          >
                            <Icon
                              icon="person"
                              sx={{ fontSize: '12px' }}
                              colorSX={theme.palette.neutral[400]}
                            />
                            {formatQuota(c)}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                          >
                            <Icon
                              icon="timer"
                              sx={{ fontSize: '12px' }}
                              colorSX={theme.palette.neutral[400]}
                            />
                            {optExpiry ?? 'Sem validade'}
                          </Box>
                        </Box>
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <Textfield
                      {...params}
                      placeholder="Buscar cupom..."
                      sx={{
                        ['& .MuiInputBase-root']: {
                          height: '100%',
                          minHeight: '36px'
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCoupons ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Box>
            )}

            {/* Associated coupons cards */}
            {associatedCoupons.length > 0 && (
              <Box>
                <Box
                  sx={{
                    display: 'grid',
                    gap: '12px',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)'
                    }
                  }}
                >
                  {associatedCoupons.map((coupon) => {
                    const preview =
                      totalAmount !== undefined && totalAmount > 0
                        ? calculateCouponDiscount(coupon, totalAmount)
                        : null
                    const discountDetail = formatDiscountDetail(coupon)
                    const expiry = formatExpiry(coupon)

                    return (
                      <Box
                        key={coupon.uuid}
                        onClick={
                          isDetails
                            ? () => setDetailsModal({ open: true, coupon })
                            : undefined
                        }
                        sx={{
                          padding: '12px 14px',
                          borderRadius: '8px',
                          border: `1px solid ${theme.palette.neutral[700]}`,
                          background: theme.palette.neutral['A200'],
                          cursor: isDetails ? 'pointer' : 'default',
                          transition: 'all 150ms linear',
                          '&:hover': isDetails
                            ? {
                                borderColor: theme.palette.neutral[500],
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                              }
                            : undefined
                        }}
                      >
                        <Stack gap={0.5}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <Typography
                              variant="smBold"
                              sx={{
                                fontSize: '16px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {coupon.code}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.25}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setDetailsModal({ open: true, coupon })
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <Icon
                                  icon="settings"
                                  sx={{ fontSize: '18px' }}
                                  colorSX={theme.palette.neutral[400]}
                                />
                              </IconButton>
                              {canEdit && !isDetails && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveClick(coupon)
                                  }}
                                  sx={{ p: 0.5 }}
                                >
                                  <Icon
                                    icon="close"
                                    sx={{ fontSize: '20px' }}
                                    colorSX={theme.palette.neutral[400]}
                                  />
                                </IconButton>
                              )}
                            </Box>
                          </Stack>

                          <Box
                            display="flex"
                            alignItems="baseline"
                            gap="6px"
                            lineHeight={1}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '18px',
                                fontWeight: 700,
                                color: theme.palette.neutral[200]
                              }}
                            >
                              {formatDiscountHeadline(coupon)}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: theme.palette.neutral[400],
                                letterSpacing: '0.4px'
                              }}
                            >
                              {coupon.discount_type === 'percentage'
                                ? 'OFF'
                                : 'FIXO'}
                            </Typography>
                            {discountDetail && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '12px',
                                  color: theme.palette.neutral[400]
                                }}
                              >
                                · {discountDetail}
                              </Typography>
                            )}
                          </Box>

                          {(() => {
                            const usagePct = getUsagePct(coupon)
                            const expiryPct = getExpiryPct(coupon)
                            const barColor = theme.palette.neutral[200]
                            const trackColor = theme.palette.neutral[800]
                            return (
                              <Stack gap={0.75} mt={0.5}>
                                <Stack gap={0.25}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{
                                      fontSize: '12px',
                                      color: theme.palette.neutral[400]
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <Icon
                                        icon="person"
                                        sx={{ fontSize: '12px' }}
                                        colorSX={theme.palette.neutral[400]}
                                      />
                                      {formatQuota(coupon)}
                                    </Box>
                                    {usagePct !== null && (
                                      <Box
                                        component="span"
                                        sx={{
                                          fontWeight: 500,
                                          color: theme.palette.neutral[300]
                                        }}
                                      >
                                        {Math.round(usagePct)}%
                                      </Box>
                                    )}
                                  </Stack>
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: '3px',
                                      borderRadius: '2px',
                                      backgroundColor: trackColor,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: `${Math.min(
                                          100,
                                          Math.max(0, usagePct ?? 0)
                                        )}%`,
                                        height: '100%',
                                        backgroundColor:
                                          usagePct !== null
                                            ? barColor
                                            : trackColor
                                      }}
                                    />
                                  </Box>
                                </Stack>

                                <Stack gap={0.25}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{
                                      fontSize: '12px',
                                      color: theme.palette.neutral[400]
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <Icon
                                        icon="timer"
                                        sx={{ fontSize: '12px' }}
                                        colorSX={theme.palette.neutral[400]}
                                      />
                                      {expiry ?? 'Sem validade'}
                                    </Box>
                                    {expiryPct !== null && (
                                      <Box
                                        component="span"
                                        sx={{
                                          fontWeight: 500,
                                          color: theme.palette.neutral[300]
                                        }}
                                      >
                                        {Math.round(expiryPct)}%
                                      </Box>
                                    )}
                                  </Stack>
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: '3px',
                                      borderRadius: '2px',
                                      backgroundColor: trackColor,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: `${Math.min(
                                          100,
                                          Math.max(0, expiryPct ?? 0)
                                        )}%`,
                                        height: '100%',
                                        backgroundColor:
                                          expiryPct !== null
                                            ? barColor
                                            : trackColor
                                      }}
                                    />
                                  </Box>
                                </Stack>
                              </Stack>
                            )
                          })()}

                          {preview && (
                            <Typography
                              variant="smLight"
                              color={theme.palette.neutral[400]}
                              fontSize="12px"
                              mt={0.5}
                            >
                              Desconto de {formatMoney(preview.discount)} —
                              valor final: {formatMoney(preview.finalValue)}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )}

            {/* Helper text when disabled */}
            {!couponsEnabled && !isDetails && associatedCoupons.length > 0 && (
              <Typography
                variant="smLight"
                color={theme.palette.neutral[400]}
                fontStyle="italic"
              >
                Ative os cupons para editar as associacoes
              </Typography>
            )}

            {/* Empty state */}
            {associatedCoupons.length === 0 && (
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                Nenhum cupom associado
              </Typography>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <CouponFormModal
        open={createCouponModalOpen}
        handleClose={() => setCreateCouponModalOpen(false)}
        mode="create"
        onSuccess={(created) => {
          if (created) {
            const updated = [...associatedCoupons, created]
            onCouponsChange?.(updated)
            if (!couponsEnabled) {
              onCouponsEnabledChange?.(true)
            }
            toast.success(`Cupom ${created.code} associado`)
          }
          refetch()
          setSearchTerm('')
        }}
      />

      <CouponDetailsModal
        open={detailsModal.open}
        handleClose={() => setDetailsModal({ open: false, coupon: null })}
        coupon={detailsModal.coupon}
        onEdit={() => setDetailsModal({ open: false, coupon: null })}
        onDuplicate={() => setDetailsModal({ open: false, coupon: null })}
        onPause={handlePause}
        onUnpause={handleUnpause}
        onFinish={handleFinish}
        onReactivate={handleReactivate}
        onExtend={() => setDetailsModal({ open: false, coupon: null })}
        onDelete={() => setDetailsModal({ open: false, coupon: null })}
      />
    </>
  )
}

export default CouponsAccordion
